import React, { useState, useEffect, useCallback } from 'react';
import {
  FaPlane, FaMapMarkedAlt, FaRoute, FaRobot, FaClock, FaHeart,
  FaGlobeAmericas, FaCompass, FaUsers, FaShieldAlt,
  FaChevronRight, FaMountain, FaUmbrellaBeach, FaCity, FaTree,
  FaMapMarkerAlt, FaTimes, FaFire, FaStar, FaQuoteLeft, FaPlay,
  FaChevronLeft
} from 'react-icons/fa';
import SiteStats from './SiteStats';
import { getApprovedTestimonials, getTrendingPlacesByRegion, getTrendingPlacesCategories, getTrendingPlaceImageUrl } from '../api/client';
import TryItNowWidget from './landing/TryItNowWidget';

// Category-specific fallback images for when place images fail to load
const CATEGORY_FALLBACK_IMAGES = {
  "Mountains": "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400&h=300&fit=crop",
  "Beaches": "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=300&fit=crop",
  "Cities": "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&h=300&fit=crop",
  "Nature": "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop",
  "Islands": "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=400&h=300&fit=crop",
  "Deserts": "https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=400&h=300&fit=crop",
  "Lakes": "https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=400&h=300&fit=crop",
  "Historical": "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&h=300&fit=crop",
  "Adventure": "https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=400&h=300&fit=crop",
  "Cultural": "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=400&h=300&fit=crop",
  "Coastal": "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=400&h=300&fit=crop",
  "Wildlife": "https://images.unsplash.com/photo-1474511320723-9a56873571b7?w=400&h=300&fit=crop",
  "Winter": "https://images.unsplash.com/photo-1483921020237-2ff51e8e4b22?w=400&h=300&fit=crop",
  "Tropical": "https://images.unsplash.com/photo-1516815231560-8f41ec531527?w=400&h=300&fit=crop",
};
const DEFAULT_FALLBACK_IMAGE = "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=300&fit=crop";

// Helper function to get category-specific fallback image
const getCategoryFallbackImage = (category) => {
  if (!category) return DEFAULT_FALLBACK_IMAGE;
  // Try exact match
  if (CATEGORY_FALLBACK_IMAGES[category]) return CATEGORY_FALLBACK_IMAGES[category];
  // Try case-insensitive match
  const categoryLower = category.toLowerCase();
  for (const [key, url] of Object.entries(CATEGORY_FALLBACK_IMAGES)) {
    if (key.toLowerCase() === categoryLower) return url;
    // Partial match (e.g., "Mountain Adventures" matches "Mountains")
    if (key.toLowerCase().includes(categoryLower) || categoryLower.includes(key.toLowerCase())) return url;
  }
  return DEFAULT_FALLBACK_IMAGE;
};

const LandingPage = ({ onGetStarted, onLogin, onViewTerms, onViewPrivacy, onViewContact, onViewAbout, onViewFaq, onViewHowTo, onViewAffiliate, onViewSample, onViewBlog, onViewPricing, onViewGuides, onViewDestinations, onViewTools, onViewTryout }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  // Trending places state
  const [trendingPlaces, setTrendingPlaces] = useState([]);
  const [categories, setCategories] = useState(['Mountains', 'Beaches', 'Cities', 'Nature']);
  const [activeCategory, setActiveCategory] = useState('Mountains');
  const [quickStartScrollIndex, setQuickStartScrollIndex] = useState(0);
  const [isLoadingTrending, setIsLoadingTrending] = useState(true);

  // Fetch trending places from API
  useEffect(() => {
    const fetchTrendingPlaces = async () => {
      setIsLoadingTrending(true);
      try {
        // Fetch categories first
        const catData = await getTrendingPlacesCategories();
        if (catData.categories && catData.categories.length > 0) {
          setCategories(catData.categories);
          setActiveCategory(catData.categories[0]);
        }

        // Fetch trending places for default region (Europe is popular)
        const data = await getTrendingPlacesByRegion('Europe');
        if (data.trending) {
          // Flatten all places from all categories into a single array
          const allPlaces = [];
          Object.entries(data.trending).forEach(([category, places]) => {
            places.forEach(place => {
              allPlaces.push({ ...place, category });
            });
          });
          setTrendingPlaces(allPlaces);
        }
      } catch (err) {
        console.error('Failed to fetch trending places:', err);
        // Keep fallback data if API fails
      } finally {
        setIsLoadingTrending(false);
      }
    };
    fetchTrendingPlaces();
  }, []);

  // Reset scroll index when category changes
  useEffect(() => {
    setQuickStartScrollIndex(0);
  }, [activeCategory]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getApprovedTestimonials();
        if (data.testimonials && data.testimonials.length > 0) {
          setTestimonials(data.testimonials);
        }
      } catch (err) {
        // Silently fail - testimonials are optional
      }
    };
    fetchTestimonials();
  }, []);

  const getVisibleCount = useCallback(() => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 3;
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => {
      setTestimonialIndex(prev => {
        const visibleCount = getVisibleCount();
        const maxIndex = Math.max(0, testimonials.length - visibleCount);
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials, getVisibleCount]);

  const features = [
    {
      icon: FaRobot,
      title: 'AI-Powered Planning',
      description: 'Our advanced AI analyzes thousands of destinations to create your perfect personalized itinerary in seconds.'
    },
    {
      icon: FaRoute,
      title: 'Optimized Routes',
      description: 'Smart routing that minimizes travel time and maximizes experiences, with options for scenic routes or efficiency.'
    },
    {
      icon: FaHeart,
      title: 'Personalized for You',
      description: 'Tell us your interests, food preferences, and travel style - we\'ll craft an itinerary that matches your vibe.'
    },
    {
      icon: FaClock,
      title: 'Save Hours of Planning',
      description: 'What used to take days of research now takes minutes. More time dreaming, less time planning.'
    },
    {
      icon: FaGlobeAmericas,
      title: 'Any Destination',
      description: 'From bustling cities to hidden gems, plan adventures anywhere in the world with local insights.'
    },
    {
      icon: FaUsers,
      title: 'Share & Collaborate',
      description: 'Easily share your itineraries with travel companions and plan together seamlessly.'
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Enter Your Details',
      description: 'Tell us where you\'re starting, your destination, dates, and what you love to do.',
      icon: FaMapMarkedAlt
    },
    {
      step: 2,
      title: 'AI Creates Your Plan',
      description: 'Our AI analyzes your preferences and generates a day-by-day itinerary optimized for you.',
      icon: FaRobot
    },
    {
      step: 3,
      title: 'Customize & Explore',
      description: 'Fine-tune your itinerary, add stops, and get ready for an unforgettable adventure.',
      icon: FaCompass
    }
  ];


  const destinations = [
    { name: 'Mountains', icon: FaMountain, color: 'from-adventure-400 to-adventure-600' },
    { name: 'Beaches', icon: FaUmbrellaBeach, color: 'from-accent-400 to-primary-500' },
    { name: 'Cities', icon: FaCity, color: 'from-coral-400 to-secondary-500' },
    { name: 'Nature', icon: FaTree, color: 'from-adventure-500 to-accent-500' }
  ];

  // Trending destinations by category
  const trendingDestinations = {
    Mountains: [
      {
        name: 'Swiss Alps, Switzerland',
        image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=400&h=300&fit=crop',
        description: 'Iconic peaks, world-class skiing, and charming alpine villages await in this breathtaking mountain paradise.',
        trend: 'Up 45% searches'
      },
      {
        name: 'Banff, Canada',
        image: 'https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=400&h=300&fit=crop',
        description: 'Turquoise lakes, dramatic peaks, and pristine wilderness in the heart of the Canadian Rockies.',
        trend: 'Top trending 2026'
      },
      {
        name: 'Dolomites, Italy',
        image: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=400&h=300&fit=crop',
        description: 'UNESCO World Heritage peaks with dramatic spires, lush meadows, and Italian alpine charm.',
        trend: 'Rising fast'
      },
      {
        name: 'Patagonia, Argentina',
        image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400&h=300&fit=crop',
        description: 'Remote wilderness with glaciers, jagged peaks, and some of the most dramatic landscapes on Earth.',
        trend: 'Adventure hotspot'
      }
    ],
    Beaches: [
      {
        name: 'Maldives',
        image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=400&h=300&fit=crop',
        description: 'Crystal-clear waters, overwater villas, and pristine coral reefs in this tropical paradise.',
        trend: 'Most searched'
      },
      {
        name: 'Bali, Indonesia',
        image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&h=300&fit=crop',
        description: 'Perfect blend of stunning beaches, vibrant culture, rice terraces, and spiritual temples.',
        trend: 'Up 60% bookings'
      },
      {
        name: 'Amalfi Coast, Italy',
        image: 'https://images.unsplash.com/photo-1533104816931-20fa691ff6ca?w=400&h=300&fit=crop',
        description: 'Dramatic cliffside villages, azure waters, and Mediterranean charm along Italy\'s stunning coastline.',
        trend: 'Summer favorite'
      },
      {
        name: 'Turks and Caicos',
        image: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=400&h=300&fit=crop',
        description: 'Pristine white sand beaches, world-class diving, and luxurious Caribbean tranquility.',
        trend: 'Hidden gem'
      }
    ],
    Cities: [
      {
        name: 'Tokyo, Japan',
        image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=300&fit=crop',
        description: 'Ultra-modern meets ancient tradition in this fascinating metropolis of technology, temples, and cuisine.',
        trend: 'Top destination 2026'
      },
      {
        name: 'Barcelona, Spain',
        image: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&h=300&fit=crop',
        description: 'Gaudi\'s masterpieces, vibrant nightlife, tapas culture, and Mediterranean beaches in one city.',
        trend: 'Up 35% interest'
      },
      {
        name: 'Dubai, UAE',
        image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&h=300&fit=crop',
        description: 'Futuristic skyline, luxury shopping, desert adventures, and world-record-breaking attractions.',
        trend: 'Fastest growing'
      },
      {
        name: 'New York City, USA',
        image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&h=300&fit=crop',
        description: 'The city that never sleeps - iconic landmarks, Broadway, diverse cuisine, and endless energy.',
        trend: 'Classic favorite'
      }
    ],
    Nature: [
      {
        name: 'Iceland',
        image: 'https://images.unsplash.com/photo-1504829857797-ddff29c27927?w=400&h=300&fit=crop',
        description: 'Northern lights, geysers, volcanic landscapes, and waterfalls in this otherworldly destination.',
        trend: 'Most unique'
      },
      {
        name: 'Costa Rica',
        image: 'https://images.unsplash.com/photo-1518259102261-b40117eabbc9?w=400&h=300&fit=crop',
        description: 'Rainforests, wildlife, volcanoes, and zip-lining through the canopy in this eco-paradise.',
        trend: 'Eco-tourism leader'
      },
      {
        name: 'New Zealand',
        image: 'https://images.unsplash.com/photo-1469521669194-babb45599def?w=400&h=300&fit=crop',
        description: 'Fjords, mountains, glowworm caves, and Middle-earth landscapes await adventure seekers.',
        trend: 'Up 50% searches'
      },
      {
        name: 'Galapagos Islands',
        image: 'https://images.unsplash.com/photo-1544979590-37e9b47eb705?w=400&h=300&fit=crop',
        description: 'Unique wildlife encounters, pristine ecosystems, and the inspiration for Darwin\'s theory of evolution.',
        trend: 'Bucket list #1'
      }
    ]
  };

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(selectedCategory === categoryName ? null : categoryName);
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-[60] bg-white/80 backdrop-blur-xl shadow-soft border-b border-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center group cursor-pointer">
              <img src="/logo.png" alt="ItinerarySeek" className="h-10 w-auto group-hover:scale-105 transition-all duration-300" />
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="/guides"
                onClick={(e) => { e.preventDefault(); onViewGuides(); }}
                className="text-neutral-700 hover:text-primary-600 font-medium transition-all duration-300 px-4 py-2 rounded-lg hover:bg-primary-50"
              >
                Travel Guides
              </a>
              <a
                href="/destinations"
                onClick={(e) => { e.preventDefault(); onViewDestinations(); }}
                className="text-neutral-700 hover:text-primary-600 font-medium transition-all duration-300 px-4 py-2 rounded-lg hover:bg-primary-50"
              >
                Destinations
              </a>
              <a
                href="/blog"
                onClick={(e) => { e.preventDefault(); onViewBlog(); }}
                className="text-neutral-700 hover:text-primary-600 font-medium transition-all duration-300 px-4 py-2 rounded-lg hover:bg-primary-50"
              >
                Blog
              </a>
              <a
                href="/pricing"
                onClick={(e) => { e.preventDefault(); onViewPricing(); }}
                className="text-neutral-700 hover:text-primary-600 font-medium transition-all duration-300 px-4 py-2 rounded-lg hover:bg-primary-50"
              >
                Pricing
              </a>
              <a
                href="/tools"
                onClick={(e) => { e.preventDefault(); onViewTools(); }}
                className="text-neutral-700 hover:text-primary-600 font-medium transition-all duration-300 px-4 py-2 rounded-lg hover:bg-primary-50"
              >
                Tools
              </a>
              <a
                href="/login"
                onClick={(e) => { e.preventDefault(); onLogin(); }}
                className="text-neutral-700 hover:text-primary-600 font-medium transition-all duration-300 px-4 py-2 rounded-lg hover:bg-primary-50"
              >
                Sign In
              </a>
              <a
                href="/register"
                onClick={(e) => { e.preventDefault(); onGetStarted(); }}
                className="bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-glow-primary hover:scale-105 transition-all duration-300 animate-gradient-x bg-[length:200%_auto]"
              >
                Get Started Free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pb-14 overflow-hidden" style={{ paddingTop: 80 }}>
        {/* Vibrant Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-accent-50/50 to-secondary-50"></div>
        <div className="absolute inset-0 bg-tropical-mesh opacity-60"></div>

        {/* Subtle Background Orbs - static for better performance */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-primary-200 to-accent-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-secondary-200 to-coral-200 rounded-full mix-blend-multiply filter blur-3xl opacity-25"></div>

        {/* Minimal Floating Icons - reduced to 2 key travel symbols */}
        <div className="hidden md:block absolute top-40 right-[12%] text-primary-300 opacity-40 animate-float-gentle">
          <FaPlane className="text-4xl transform rotate-12 drop-shadow-sm" />
        </div>
        <div className="hidden md:block absolute bottom-32 left-[15%] text-secondary-300 opacity-35 animate-float-gentle" style={{animationDelay: '2s'}}>
          <FaGlobeAmericas className="text-5xl drop-shadow-sm" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-6 animate-fade-in">
              <span className="h-px w-16 bg-gradient-to-r from-transparent via-primary-400 to-accent-400"></span>
              <span className="px-4 py-1.5 bg-gradient-to-r from-primary-100 to-accent-100 rounded-full text-sm uppercase tracking-widest font-semibold flex items-center gap-2 text-primary-700 shadow-soft">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-adventure-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-adventure-500"></span>
                </span>
                AI-Powered Travel Planning
              </span>
              <span className="h-px w-16 bg-gradient-to-l from-transparent via-accent-400 to-secondary-400"></span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold font-display mb-6 animate-slide-up">
              <span className="text-neutral-800 drop-shadow-sm">Your Perfect Trip,</span>
              <br />
              <span className="bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500 bg-clip-text text-transparent animate-gradient-x bg-[length:200%_auto]">
                Planned in Seconds
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-neutral-600 mb-8 max-w-2xl mx-auto animate-slide-up leading-relaxed" style={{animationDelay: '0.1s'}}>
              Let AI create your dream itinerary. Personalized routes, hidden gems, and <span className="text-adventure-600 font-semibold">unforgettable experiences</span> - all tailored to you.
            </p>

            <p className="text-sm font-medium text-neutral-500 mb-3 animate-slide-up" style={{animationDelay: '0.12s'}}>
              Free, no signup — see real attractions, photos, and a Day-1 plan for any destination.
            </p>

            <TryItNowWidget renderResults={false} onPreviewReady={onViewTryout} />


          </div>
        </div>
      </section>

      {/* Editorial intro — substantive prose content for SEO / AdSense content density */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-neutral max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-neutral-800 mb-6">
              Travel planning, rethought from the ground up
            </h2>
            <p className="text-lg text-neutral-700 leading-relaxed mb-5">
              Most trip planning still looks the same as it did a decade ago: a
              dozen browser tabs, a half-finished spreadsheet, a group chat
              nobody reads, and a Google Doc that gets out of date the moment
              someone's flight shifts. ItinerarySeek was built because that
              workflow fails the travelers who need planning the most — the
              ones trying to stretch a real budget, coordinate a multi-city
              route, or fit everything that actually matters into the handful
              of days they can get away.
            </p>
            <p className="text-lg text-neutral-700 leading-relaxed mb-5">
              Instead of dropping you into a blank form, we start from what a
              trip actually requires: where you're going, how long you have,
              who you're traveling with, what you care about, and what you're
              willing to spend. The AI works from those constraints outward —
              sequencing days so you aren't crossing a city twice, grouping
              neighborhoods so you spend time exploring instead of in transit,
              weighing opening hours and crowd patterns, and flagging the
              trade-offs so you can make the call rather than hoping for the
              best.
            </p>
            <h3 className="text-2xl font-bold text-neutral-800 mt-10 mb-4">
              What "AI-powered" actually means here
            </h3>
            <p className="text-lg text-neutral-700 leading-relaxed mb-5">
              We're specific about this because the phrase is overused. The
              planner runs on large language models that have been given a
              structured view of your trip context, access to destination data,
              and a set of guardrails that keep recommendations grounded. It
              doesn't invent restaurants that don't exist and it doesn't route
              you through neighborhoods that are closed for the season. When
              the model isn't sure, it tells you — and it tells you why.
            </p>
            <p className="text-lg text-neutral-700 leading-relaxed mb-5">
              Everything is editable. The itinerary it produces is a starting
              point, not a verdict. Swap days, drop activities, change the
              pace, add a stop, ask for a vegetarian-friendly food plan on
              Thursday instead of Wednesday — the plan regenerates around your
              edits instead of starting from scratch. This is the part that
              spreadsheets and static "top 10" lists can't touch.
            </p>
            <h3 className="text-2xl font-bold text-neutral-800 mt-10 mb-4">
              Built for real trips, not demo trips
            </h3>
            <p className="text-lg text-neutral-700 leading-relaxed mb-5">
              The travelers who stuck with us through the early builds shaped
              what ItinerarySeek looks like today: families coordinating across
              time zones, solo travelers on tight budgets, couples trying to
              balance two different ideas of a good day, small groups of
              friends who wanted the trip to actually feel planned without any
              one person becoming the permanent trip manager. Their feedback
              pushed us toward things that matter in practice — offline-ready
              day views, shareable plans, honest cost breakdowns, route maps
              that reflect real opening hours, and the ability to go from idea
              to workable itinerary in the time it takes to finish a coffee.
            </p>
            <p className="text-lg text-neutral-700 leading-relaxed">
              The result is a planner you can actually ship a trip with. Not a
              novelty, not a party trick, not a chatbot with a travel coat of
              paint. A working tool for the part of travel nobody enjoys so you
              can spend more of your attention on the part you do.
            </p>
            <div className="mt-8 pt-6 border-t border-neutral-200">
              <p className="text-base text-neutral-600">
                <a href="/howto" onClick={(e) => { e.preventDefault(); onViewHowTo(); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">Learn how to get started</a>
                {' · '}
                <a href="/pricing" onClick={(e) => { e.preventDefault(); onViewPricing(); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">View pricing plans</a>
                {' · '}
                <a href="/faq" onClick={(e) => { e.preventDefault(); onViewFaq(); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">Read our FAQ</a>
                {' · '}
                <a href="/about" onClick={(e) => { e.preventDefault(); onViewAbout(); }} className="text-primary-600 hover:text-primary-700 font-medium hover:underline">About us</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Guides section — links to blog content, required for AdSense content depth */}
      <section className="py-16 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-neutral-800 mb-4">
              From the ItinerarySeek travel desk
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              In-depth guides, planning frameworks, and destination reporting
              from our editorial team. Every article is grounded in current
              news, specific places, and concrete prices — not generic tips.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white rounded-xl p-6 shadow-soft border border-neutral-200">
              <h3 className="font-bold text-neutral-800 mb-2">Destination guides</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Neighborhood-by-neighborhood breakdowns, seasonal timing, and
                the trade-offs between the obvious and the worth-it choices.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-soft border border-neutral-200">
              <h3 className="font-bold text-neutral-800 mb-2">Planning frameworks</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                How to think about budget vs. pace, how to sequence a
                multi-city route, and how to avoid the traps that wreck trips
                before they start.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-soft border border-neutral-200">
              <h3 className="font-bold text-neutral-800 mb-2">Current travel news</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">
                What's actually changing — entry rules, new routes, seasonal
                closures, and the reporting that should change how you plan
                right now.
              </p>
            </div>
          </div>
          <div className="text-center">
            <a
              href="/blog"
              className="inline-block px-8 py-3 bg-neutral-800 text-white font-semibold rounded-lg hover:bg-neutral-900 transition-colors"
            >
              Read the blog
            </a>
          </div>
        </div>
      </section>

      {/* Intro Video Section */}
      <section className="py-20 bg-gradient-to-b from-neutral-50 via-white to-white relative overflow-hidden">
        <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-br from-primary-200 to-accent-200 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-gradient-to-br from-secondary-200 to-coral-200 rounded-full blur-3xl opacity-20"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
              <FaPlay className="text-primary-600 text-xs" />
              <span className="text-primary-700 font-semibold text-sm uppercase tracking-wide">Watch How It Works</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-display text-neutral-800 mb-5">
              Your Dream Trip, <span className="text-gradient-tropical">Planned in Seconds</span>
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
              Watch how ItinerarySeek transforms a simple destination into a fully personalized,
              day-by-day travel itinerary — powered entirely by AI. No templates, no guesswork,
              just your perfect trip.
            </p>
          </div>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-neutral-200/50 bg-neutral-900">
            <video
              className="w-full aspect-video"
              controls
              preload="metadata"
              playsInline
              poster=""
            >
              <source src="/media/Intro-Video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 mt-10 text-sm text-neutral-500">
            <div className="flex items-center gap-2">
              <FaClock className="text-primary-500" />
              <span>See results in under 60 seconds</span>
            </div>
            <div className="flex items-center gap-2">
              <FaRobot className="text-accent-500" />
              <span>100% AI-generated itineraries</span>
            </div>
            <div className="flex items-center gap-2">
              <FaRoute className="text-secondary-500" />
              <span>Personalized to your preferences</span>
            </div>
          </div>
        </div>
      </section>

      {/* See It In Action Section */}
      <section className="py-16 bg-gradient-to-b from-white via-primary-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-accent-200 to-primary-200 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-36 h-36 bg-gradient-to-br from-secondary-200 to-golden-200 rounded-full blur-3xl opacity-30"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 rounded-full mb-6">
              <FaCompass className="text-accent-600" />
              <span className="text-accent-700 font-semibold text-sm uppercase tracking-wide">Live Preview</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-display text-neutral-800 mb-5">
              See It <span className="text-gradient-tropical">In Action</span>
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              Explore real sample itineraries — no account needed. See exactly what you'll get.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                slug: 'tokyo-japan',
                name: 'Tokyo, Japan',
                category: 'Cities',
                image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=300&fit=crop',
                description: 'Temples, technology & world-class cuisine in 2 action-packed days.',
                icon: FaCity,
                gradient: 'from-coral-400 to-secondary-500'
              },
              {
                slug: 'bali-indonesia',
                name: 'Bali, Indonesia',
                category: 'Beaches',
                image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&h=300&fit=crop',
                description: 'Rice terraces, sacred temples & stunning beach sunsets.',
                icon: FaUmbrellaBeach,
                gradient: 'from-accent-400 to-primary-500'
              },
              {
                slug: 'barcelona-spain',
                name: 'Barcelona, Spain',
                category: 'Cities',
                image: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&h=300&fit=crop',
                description: 'Gaudi masterpieces, tapas crawls & Mediterranean vibes.',
                icon: FaCity,
                gradient: 'from-coral-400 to-secondary-500'
              },
              {
                slug: 'swiss-alps-switzerland',
                name: 'Swiss Alps',
                category: 'Mountains',
                image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=400&h=300&fit=crop',
                description: 'Alpine peaks, scenic trains & adventure sports at altitude.',
                icon: FaMountain,
                gradient: 'from-adventure-400 to-adventure-600'
              }
            ].map((sample) => (
              <div
                key={sample.slug}
                className="group bg-white rounded-2xl shadow-card hover:shadow-card-hover overflow-hidden transition-all duration-500 hover:-translate-y-3 cursor-pointer border border-neutral-100/50"
                onClick={() => onViewSample && onViewSample(sample.slug)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={sample.image}
                    alt={sample.name}
                    className="w-full h-full object-cover group-hover:scale-115 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <div className="absolute top-3 left-3">
                    <span className={`inline-flex items-center gap-1.5 bg-gradient-to-r ${sample.gradient} text-xs font-bold text-white px-3 py-1.5 rounded-full shadow-md`}>
                      <sample.icon className="text-xs" />
                      {sample.category}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-xs font-bold text-primary-600 px-2.5 py-1 rounded-full shadow-sm">
                      2 Days
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-neutral-900/20 to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="flex items-center gap-2 text-white">
                      <FaMapMarkerAlt className="text-secondary-400 drop-shadow-md" />
                      <span className="font-bold text-lg drop-shadow-lg">{sample.name}</span>
                    </div>
                  </div>
                </div>
                <div className="p-5 bg-gradient-to-br from-white to-neutral-50">
                  <p className="text-neutral-600 text-sm leading-relaxed mb-4">{sample.description}</p>
                  <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
                    <span className="text-xs text-neutral-400 font-medium">No signup required</span>
                    <span className="flex items-center gap-1 text-primary-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                      View Sample
                      <FaChevronRight className="text-xs group-hover:text-accent-500" />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verifiable counts, sitting where invented social proof usually goes.
          Real rows, counted on request — see SiteStats. */}
      <SiteStats />

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="relative py-10 overflow-hidden">
          {/* Theme-matching gradient: deep ocean blue → teal → hints of sunset */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-800 via-accent-800 to-primary-900"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(6,182,212,0.25),_transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(249,115,22,0.1),_transparent_50%)]"></div>

          {/* Decorative sparkles using theme colors */}
          <div className="absolute top-6 left-[10%] w-2 h-2 bg-accent-300 rounded-full animate-pulse opacity-60"></div>
          <div className="absolute top-12 right-[15%] w-1.5 h-1.5 bg-golden-300 rounded-full animate-pulse opacity-40" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-8 left-[25%] w-1.5 h-1.5 bg-primary-300 rounded-full animate-pulse opacity-50" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-14 right-[20%] w-2 h-2 bg-accent-400 rounded-full animate-pulse opacity-40" style={{animationDelay: '0.5s'}}></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Compact header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="h-px w-8 bg-gradient-to-r from-transparent to-accent-400/60"></span>
                <FaQuoteLeft className="text-golden-400 text-lg" />
                <span className="text-accent-300/80 text-xs font-semibold uppercase tracking-[0.2em]">Testimonials</span>
                <span className="h-px w-8 bg-gradient-to-l from-transparent to-accent-400/60"></span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-display text-white">
                Loved by <span className="bg-gradient-to-r from-golden-300 via-secondary-300 to-golden-400 bg-clip-text text-transparent">Travelers</span> Worldwide
              </h2>
            </div>

            {/* Testimonial cards carousel */}
            <div className="relative overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${testimonialIndex * (100 / (typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3))}%)` }}
              >
                {testimonials.map((t, idx) => (
                  <div
                    key={idx}
                    className="w-full sm:w-1/2 lg:w-1/3 flex-shrink-0 px-2.5"
                  >
                    <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/20 hover:bg-white/[0.15] hover:border-accent-400/30 hover:-translate-y-1 transition-all duration-300 h-full flex flex-col group">
                      {/* Glow effect on hover */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-accent-400/0 to-golden-400/0 group-hover:from-accent-400/5 group-hover:to-golden-400/5 transition-all duration-300"></div>

                      <div className="relative">
                        {/* Star rating row */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(i => (
                              <FaStar key={i} className={`text-sm ${i <= t.rating ? 'text-golden-400 drop-shadow-[0_0_3px_rgba(245,158,11,0.4)]' : 'text-white/20'}`} />
                            ))}
                          </div>
                          {t.would_recommend && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-adventure-500/20 text-adventure-300 text-[10px] font-semibold border border-adventure-400/20">
                              <FaHeart className="text-[8px]" /> Recommends
                            </span>
                          )}
                        </div>

                        {/* Quote */}
                        <div className="relative mb-3">
                          <FaQuoteLeft className="absolute -top-1 -left-1 text-golden-400/20 text-xl" />
                          <p className="text-white/90 text-sm leading-relaxed pl-5">
                            {t.comment}
                          </p>
                        </div>

                        {/* Author */}
                        <div className="flex items-center gap-3 pt-3 border-t border-white/10">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary-400 to-coral-500 flex items-center justify-center text-white text-xs font-bold shadow-lg shadow-secondary-500/20">
                            {t.first_name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-semibold text-white text-sm">{t.first_name}</p>
                            {t.destination && (
                              <p className="text-[11px] text-white/50 flex items-center gap-1">
                                <FaMapMarkerAlt className="text-accent-400/60 text-[9px]" />
                                {t.destination}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots indicator */}
            {testimonials.length > (typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3) && (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: Math.max(1, testimonials.length - (typeof window !== 'undefined' && window.innerWidth < 640 ? 0 : window.innerWidth < 1024 ? 1 : 2)) }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTestimonialIndex(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === testimonialIndex ? 'bg-golden-400 w-6' : 'bg-white/30 w-1.5 hover:bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Quick Start - Choose Your Adventure */}
      <section className="py-16 bg-gradient-to-b from-white via-accent-50/30 to-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-primary-200 to-accent-200 rounded-full blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-secondary-200 to-golden-200 rounded-full blur-3xl opacity-40"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-secondary-100 to-coral-100 rounded-full mb-4">
              <FaCompass className="text-secondary-600" />
              <span className="text-secondary-700 font-semibold text-sm uppercase tracking-wide">Quick Start</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-display text-neutral-800 mb-3">
              Choose Your <span className="text-gradient-tropical">Adventure</span>
            </h2>
            <p className="text-neutral-600 text-lg">Discover trending destinations and start planning your dream trip</p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {destinations.map((dest, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(dest.name)}
                className={`group flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeCategory === dest.name
                    ? `bg-gradient-to-r ${dest.color} text-white shadow-elevated scale-105`
                    : 'bg-white text-neutral-600 shadow-soft hover:shadow-card hover:scale-102 border border-neutral-200'
                }`}
              >
                <dest.icon className={`text-lg ${activeCategory === dest.name ? '' : 'text-neutral-400 group-hover:text-neutral-600'}`} />
                <span>{dest.name}</span>
              </button>
            ))}
          </div>

          {/* Scrollable Places Grid - 2 rows of 4 */}
          <div className="relative">
            {/* Navigation Arrows */}
            {(() => {
              const filteredPlaces = trendingPlaces.filter(p => p.category === activeCategory);
              const totalPages = Math.ceil(filteredPlaces.length / 8);
              const canScrollLeft = quickStartScrollIndex > 0;
              const canScrollRight = quickStartScrollIndex < totalPages - 1;

              return (
                <>
                  {canScrollLeft && (
                    <button
                      onClick={() => setQuickStartScrollIndex(prev => Math.max(0, prev - 1))}
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-full shadow-elevated flex items-center justify-center text-neutral-600 hover:text-primary-600 hover:scale-110 transition-all duration-300"
                    >
                      <FaChevronLeft className="text-lg" />
                    </button>
                  )}
                  {canScrollRight && (
                    <button
                      onClick={() => setQuickStartScrollIndex(prev => prev + 1)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white/95 backdrop-blur-sm rounded-full shadow-elevated flex items-center justify-center text-neutral-600 hover:text-primary-600 hover:scale-110 transition-all duration-300"
                    >
                      <FaChevronRight className="text-lg" />
                    </button>
                  )}
                </>
              );
            })()}

            {/* Places Grid Container with max height for scrolling */}
            <div className="overflow-hidden">
              {isLoadingTrending ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
                </div>
              ) : (
                <div
                  className="transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${quickStartScrollIndex * 100}%)` }}
                >
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {/* Show 8 places at a time (2 rows x 4 columns) */}
                    {(() => {
                      const filteredPlaces = trendingPlaces.filter(p => p.category === activeCategory);
                      const startIdx = quickStartScrollIndex * 8;
                      const visiblePlaces = filteredPlaces.slice(startIdx, startIdx + 8);

                      // Fallback to hardcoded data if no API data
                      const displayPlaces = visiblePlaces.length > 0 ? visiblePlaces : trendingDestinations[activeCategory]?.slice(0, 8) || [];

                      return displayPlaces.map((place, index) => (
                        <div
                          key={place.id || index}
                          className="group bg-white rounded-2xl shadow-card hover:shadow-card-hover overflow-hidden transition-all duration-500 hover:-translate-y-2 cursor-pointer border border-neutral-100/50"
                          onClick={onGetStarted}
                        >
                          {/* Image Container */}
                          <div className="relative h-36 md:h-44 overflow-hidden">
                            <img
                              src={place.image_url || place.image || (place.id ? getTrendingPlaceImageUrl(place.id) : '')}
                              alt={place.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              onError={(e) => {
                                e.target.src = getCategoryFallbackImage(place.category || activeCategory);
                              }}
                            />
                            {/* Shimmer Effect on Hover */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            {/* Trend Badge */}
                            <div className="absolute top-2 left-2">
                              <span className="inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-xs font-bold text-secondary-600 px-2 py-1 rounded-full shadow-soft">
                                <FaFire className="text-secondary-500 text-xs" />
                                <span className="hidden sm:inline">{place.trend || 'Trending'}</span>
                              </span>
                            </div>
                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/80 via-neutral-900/20 to-transparent"></div>
                            {/* Location Name on Image */}
                            <div className="absolute bottom-2 left-2 right-2">
                              <div className="flex items-center gap-1.5 text-white">
                                <FaMapMarkerAlt className="text-secondary-400 text-xs drop-shadow-md" />
                                <span className="font-bold text-sm md:text-base drop-shadow-lg truncate">{place.name}</span>
                              </div>
                            </div>
                          </div>

                          {/* Content - Minimal for cleaner look */}
                          <div className="p-3 md:p-4 bg-gradient-to-br from-white to-neutral-50">
                            <p className="text-neutral-600 text-xs md:text-sm leading-relaxed line-clamp-2 mb-2">
                              {place.description || 'Discover amazing experiences at this trending destination.'}
                            </p>
                            <div className="flex items-center justify-end">
                              <span className="flex items-center gap-1 text-primary-600 font-semibold text-xs md:text-sm group-hover:gap-2 transition-all duration-300">
                                Explore
                                <FaChevronRight className="text-xs group-hover:text-accent-500" />
                              </span>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Pagination Dots */}
            {(() => {
              const filteredPlaces = trendingPlaces.filter(p => p.category === activeCategory);
              const totalPages = Math.ceil(filteredPlaces.length / 8);

              if (totalPages <= 1) return null;

              return (
                <div className="flex justify-center gap-2 mt-6">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setQuickStartScrollIndex(i)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        i === quickStartScrollIndex ? 'bg-primary-500 w-8' : 'bg-neutral-300 w-2 hover:bg-neutral-400'
                      }`}
                    />
                  ))}
                </div>
              );
            })()}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-10">
            <button
              onClick={onGetStarted}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-primary-500 via-accent-500 to-adventure-500 text-white px-10 py-4 rounded-2xl font-semibold text-lg shadow-elevated hover:shadow-glow-primary hover:scale-105 transition-all duration-300"
            >
              Start Planning Your Adventure
              <FaChevronRight />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-white via-primary-50/30 to-white relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 right-0 w-72 h-72 bg-gradient-to-br from-accent-200 to-primary-200 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-20 left-0 w-64 h-64 bg-gradient-to-br from-secondary-200 to-golden-200 rounded-full blur-3xl opacity-30"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
              <FaRobot className="text-primary-600" />
              <span className="text-primary-700 font-semibold text-sm uppercase tracking-wide">Powerful Features</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-display text-neutral-800 mb-5">
              Why Choose <span className="text-gradient-tropical">ItinerarySeek</span>?
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              Experience the future of travel planning with features designed to make every journey <span className="text-adventure-600 font-semibold">extraordinary</span>.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white/80 backdrop-blur-sm p-8 rounded-3xl shadow-card hover:shadow-card-hover border border-neutral-100/50 transition-all duration-500 hover:-translate-y-2 hover:bg-white"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-400 via-accent-400 to-adventure-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-md">
                  <feature.icon className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold font-display text-neutral-800 mb-3">{feature.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 via-accent-600 to-adventure-600 relative overflow-hidden">
        {/* Subtle Background Pattern - static decorative elements */}
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-16 left-16"><FaPlane className="text-8xl" /></div>
          <div className="absolute bottom-16 right-16"><FaGlobeAmericas className="text-8xl" /></div>
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/20 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
              <FaRoute className="text-golden-400" />
              <span className="text-white/90 font-semibold text-sm uppercase tracking-wide">Simple Process</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-display text-white mb-5">
              How It Works
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Three simple steps to your <span className="text-golden-400 font-semibold">perfect adventure</span>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative group">
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-1 bg-gradient-to-r from-white/40 to-white/10 rounded-full"></div>
                )}
                <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-500 hover:-translate-y-2 hover:shadow-glow-primary">
                  <div className="w-18 h-18 bg-gradient-to-br from-white to-neutral-100 rounded-2xl flex items-center justify-center mb-6 shadow-elevated group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 w-16 h-16">
                    <step.icon className="text-3xl text-primary-500" />
                  </div>
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-golden-400 to-secondary-500 text-white font-bold text-lg mb-4 shadow-md">
                    {step.step}
                  </div>
                  <h3 className="text-2xl font-bold font-display text-white mb-3">{step.title}</h3>
                  <p className="text-white/80 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-neutral-900 to-neutral-950 py-16 relative overflow-hidden">
        {/* Decorative gradient line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center group cursor-pointer">
              <img src="/logo.png" alt="ItinerarySeek" className="h-10 w-auto group-hover:scale-105 transition-all duration-300" />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-neutral-400">
              <a
                href="/about"
                onClick={(e) => { e.preventDefault(); onViewAbout && onViewAbout(); }}
                className="hover:text-primary-400 transition-all duration-300 font-medium"
              >
                About Us
              </a>
              <a
                href="/faq"
                onClick={(e) => { e.preventDefault(); onViewFaq && onViewFaq(); }}
                className="hover:text-golden-400 transition-all duration-300 font-medium"
              >
                FAQ
              </a>
              <a
                href="/howto"
                onClick={(e) => { e.preventDefault(); onViewHowTo && onViewHowTo(); }}
                className="hover:text-accent-400 transition-all duration-300 font-medium"
              >
                How To
              </a>
              <a
                href="/tryout"
                onClick={(e) => { e.preventDefault(); onViewTryout && onViewTryout(); }}
                className="hover:text-primary-400 transition-all duration-300 font-medium"
              >
                Free Trip Preview
              </a>
              <a
                href="/blog"
                onClick={(e) => { e.preventDefault(); onViewBlog && onViewBlog(); }}
                className="hover:text-secondary-400 transition-all duration-300 font-medium"
              >
                Blog
              </a>
              <a
                href="/pricing"
                onClick={(e) => { e.preventDefault(); onViewPricing && onViewPricing(); }}
                className="hover:text-coral-400 transition-all duration-300 font-medium"
              >
                Pricing
              </a>
              <a
                href="/terms"
                onClick={(e) => { e.preventDefault(); onViewTerms && onViewTerms(); }}
                className="hover:text-adventure-400 transition-all duration-300 font-medium"
              >
                Terms of Service
              </a>
              <a
                href="/privacy"
                onClick={(e) => { e.preventDefault(); onViewPrivacy && onViewPrivacy(); }}
                className="hover:text-adventure-400 transition-all duration-300 font-medium"
              >
                Privacy Policy
              </a>
              <a
                href="/contact"
                onClick={(e) => { e.preventDefault(); onViewContact && onViewContact(); }}
                className="hover:text-secondary-400 transition-all duration-300 font-medium"
              >
                Contact Us
              </a>
              <a
                href="/affiliate"
                onClick={(e) => { e.preventDefault(); onViewAffiliate && onViewAffiliate(); }}
                className="hover:text-accent-400 transition-all duration-300 font-medium"
              >
                Affiliate
              </a>
              <a
                href="/guides"
                onClick={(e) => { e.preventDefault(); onViewGuides && onViewGuides(); }}
                className="hover:text-primary-400 transition-all duration-300 font-medium"
              >
                Travel Guides
              </a>
              <a
                href="/destinations"
                onClick={(e) => { e.preventDefault(); onViewDestinations && onViewDestinations(); }}
                className="hover:text-accent-400 transition-all duration-300 font-medium"
              >
                Destinations
              </a>
              <a
                href="/login"
                onClick={(e) => { e.preventDefault(); onLogin && onLogin(); }}
                className="hover:text-primary-400 transition-all duration-300 font-medium"
              >
                Sign In
              </a>
              <a
                href="/register"
                onClick={(e) => { e.preventDefault(); onGetStarted && onGetStarted(); }}
                className="hover:text-accent-400 transition-all duration-300 font-medium"
              >
                Sign Up
              </a>
            </div>

            <p className="text-neutral-500 text-sm flex items-center gap-2">
              <FaHeart className="text-coral-500 text-xs" />
              © {new Date().getFullYear()} ItinerarySeek. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
